/* eslint-disable import/no-cycle */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Hamburger from 'hamburger-react';

import getLink from '@repo/utils/getInternalLink';
import styles from '@repo/ui-components/styles';
import { useUiComponentsInternationalContext } from '@repo/static-translations';

import LinkComponent from '../LinkComponent';
import breakpoints from '../breakpoints';
import NavMobile from './NavMobile';
import NavDesktop from './NavDesktop';
import MegaMenu from './MegaMenu';
import NavSearch from './NavSearch';
import IconProfile from '../svgs/user-profile-icon.svg';
import CartIcon from './CartIcon';
import DealerSearchTrigger from '../dealer-search/DealerSearchTrigger';

const Wrapper = styled.header`
  background: ${({ theme }) => theme.colors.headerBackground};
  display: flex;
  align-items: center;
  flex-direction: column;
  position: relative;
  z-index: 2;
`;

const Inner = styled.div`
  background: inherit;
  display: flex;
  align-items: center;
  padding: ${styles.spacing['spacing-md']};
  max-width: ${breakpoints.xl};
  width: 100%;
  z-index: 2;
  ${props => props.minimal && 'justify-content: center;'}
`;

const MobileLogo = styled(LinkComponent)`
  display: flex;
  flex: 1;
  ${({ theme }) =>
    theme.images.mobileLogo
      ? `background-image: url(${theme.images.mobileLogo});`
      : `background-image: url(${theme.images.logo});`}
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  height: ${({ theme }) => theme.logoHeight};
  @media (min-width: ${breakpoints.lg}) {
    display: none;
  }
`;

const DesktopLogo = styled(LinkComponent)`
  display: none;
  background-image: url(${({ theme }) => theme.images.logo});
  background-repeat: no-repeat;
  background-position: left center;
  background-size: 200px 120px;
  height: 90px;
  @media (min-width: ${breakpoints.lg}) {
    display: flex;
    width: 200px;
  }
  @media (min-width: ${breakpoints.xl}) {
  }
`;

const MobileNavTrigger = styled.div`
  width: 48px;
  height: 48px;

  @media (min-width: ${breakpoints.lg}) {
    display: none;
  }

  .hamburger-react div {
    background: ${({ theme }) => theme.colors.defaultFont} !important;
  }
`;

const NavDesktopAccount = styled.div`
  display: none;
  @media (min-width: ${breakpoints.lg}) {
    display: flex;
  }
  svg {
    transition: all 0.5s ease;
    fill: ${({ theme }) => theme.colors.defaultFont};
  }
  &:hover {
    svg {
      fill: ${({ theme }) => theme.colors.defaultFont};
    }
  }
`;

const AccountLink = styled(LinkComponent)`
  display: flex;
  font-size: inherit;
  margin-top: 1rem;
  padding: 1.5rem 1rem;
  padding-left: 0.75rem;
  border-top: solid 1px inherit;
  align-items: center;
  color: inherit;
  text-decoration: none;
  font-weight: inherit;

  svg {
    fill: ${({ theme }) => theme.colors.defaultFont};
  }

  @media (min-width: ${breakpoints.lg}) {
    border: none;
    margin-top: 0;
  }
  span {
    margin-top: -5px;
    padding-left: 0.5rem;
    @media (min-width: ${breakpoints.lg}) {
      display: none;
    }
  }
`;

const SearchContainer = styled.div`
  display: none;
  position: relative;
  width: 14rem;
  @media (min-width: ${breakpoints.lg}) {
    display: block;
  }

  @media (min-width: ${breakpoints.xl}) {
    width: 25rem;
  }
`;

const DealerSearchTriggerContainer = styled.div`
  background: ${({ theme }) => theme.colors.brandPrimary};
  width: 100%;
  @media (min-width: ${breakpoints.lg}) {
    display: none;
  }
`;

function MinimalHeader({
  currency = 'USD',
  brandName,
  hideFromScreenReader = false,
}) {
  const homepageUrl = getLink({ slug: '/', currency });
  const { tt } = useUiComponentsInternationalContext();

  const goToHomepageLabel = brandName
    ? tt(s => s.headerCommon.linkToBrandHomePageAriaLabel, { brandName })
    : tt(s => s.headerCommon.linkToHomePageAriaLabel);

  return (
    <Wrapper aria-hidden={hideFromScreenReader}>
      <Inner minimal>
        <MobileLogo
          to={homepageUrl}
          data-cy="mobile-logo"
          aria-label={goToHomepageLabel}
        />
        <DesktopLogo
          to={homepageUrl}
          data-cy="desktop-logo"
          aria-label={goToHomepageLabel}
        />
      </Inner>
    </Wrapper>
  );
}

function Header({
  menuStructure,
  searchIndexes = [],
  flags = {},
  hasDealerSearch = false,
  getInternalLink = () => {},
  searchHitsPerPage = 5,
  currency = 'USD',
  brandName,
  hideFromScreenReader = false,
}) {
  const { tt } = useUiComponentsInternationalContext();
  const [mobileMenuActive, setMobileMenuActive] = useState(false);
  const [megaMenuActive, setMegaMenuActive] = useState(null);
  const [keyboardNavActive, setKeyboardNavActive] = useState(false);

  const navLinks = menuStructure?.filter(section => section.menuType);

  const homepageUrl = getLink({ slug: '/', currency });

  const goToHomepageLabel = brandName
    ? tt(s => s.headerCommon.linkToBrandHomePageAriaLabel, { brandName })
    : tt(s => s.headerCommon.linkToHomePageAriaLabel);

  const getInternalLinkWithCurrency = props =>
    getInternalLink({
      ...props,
      currency,
    });

  return (
    <Wrapper aria-hidden={hideFromScreenReader}>
      <Inner>
        <MobileNavTrigger>
          <Hamburger
            size={24}
            toggled={mobileMenuActive}
            toggle={setMobileMenuActive}
            label={tt(s => s.headerCommon.mobileNavigationHamburgerLabel)}
          />
        </MobileNavTrigger>
        <MobileLogo
          to={homepageUrl}
          data-cy="mobile-logo"
          aria-label={goToHomepageLabel}
        />
        <DesktopLogo
          to={homepageUrl}
          data-cy="desktop-logo"
          aria-label={goToHomepageLabel}
        />
        <>
          <NavDesktop
            navLinks={navLinks}
            setMegaMenuActive={setMegaMenuActive}
            megaMenuActive={megaMenuActive}
            setKeyboardNavActive={setKeyboardNavActive}
            keyboardNavActive={keyboardNavActive}
            flags={flags}
            getInternalLink={getInternalLinkWithCurrency}
          />
          {navLinks?.map(
            (shopSection, id) =>
              shopSection.menuType === 'megaMenu' &&
              shopSection.items?.length && (
                <MegaMenu
                  key={`mega-${shopSection?.title}}`}
                  megaMenuIndex={id}
                  megaMenuActive={megaMenuActive}
                  setMegaMenuActive={setMegaMenuActive}
                  keyboardNavActive={keyboardNavActive}
                  menuStructure={shopSection}
                  columns={shopSection.columns}
                  getInternalLink={getInternalLinkWithCurrency}
                />
              )
          )}

          <SearchContainer>
            <NavSearch
              searchIndexes={searchIndexes}
              hitsPerPage={searchHitsPerPage}
            />
          </SearchContainer>
        </>

        <NavDesktopAccount>
          <AccountLink
            to={getLink({ slug: '/account', currency })}
            title={tt(s => s.headerCommon.myAccountLink)}
            aria-label={tt(s => s.headerCommon.myAccountAriaLabel)}
          >
            <IconProfile width={21} height={21} />
          </AccountLink>
        </NavDesktopAccount>
        <CartIcon />
      </Inner>
      <NavMobile
        mobileMenuActive={mobileMenuActive}
        navLinks={navLinks}
        accountLinkComponent={AccountLink}
        flags={flags}
        searchIndexes={searchIndexes}
        getInternalLink={getInternalLinkWithCurrency}
        searchHitsPerPage={searchHitsPerPage}
      />

      {hasDealerSearch && (
        <DealerSearchTriggerContainer>
          <DealerSearchTrigger />
        </DealerSearchTriggerContainer>
      )}
    </Wrapper>
  );
}

MinimalHeader.propTypes = {
  currency: PropTypes.string,
  brandName: PropTypes.string,
  hideFromScreenReader: PropTypes.bool,
};

Header.propTypes = {
  hasDealerSearch: PropTypes.bool,
  menuStructure: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      link: PropTypes.shape({}),
      menuType: PropTypes.string,
      megaMenuColumns: PropTypes.number,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          link: PropTypes.shape({}),
          subSections: PropTypes.arrayOf(
            PropTypes.shape({
              name: PropTypes.string,
              link: PropTypes.string,
            })
          ),
        })
      ),
    })
  ).isRequired,
  searchIndexes: PropTypes.arrayOf(PropTypes.string),
  flags: PropTypes.shape({}),
  getInternalLink: PropTypes.func,
  searchHitsPerPage: PropTypes.number,
  currency: PropTypes.string,
  brandName: PropTypes.string,
  hideFromScreenReader: PropTypes.bool,
};

Header.defaultProps = {
  currency: 'USD',
  brandName: '',
};

MinimalHeader.defaultProps = {
  brandName: '',
};

export { MinimalHeader, Header };

export default Header;
